import React, { useEffect, useState, useRef } from 'react';
import { Card, Input, Button, Space, Modal, message, Typography, Tag, Row, Col, Upload, Tooltip, App } from 'antd';
import { useLocation } from 'react-router-dom';
import {
  EditOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  SaveOutlined,
  PlusOutlined,
  UploadOutlined,
  DownloadOutlined,
  SyncOutlined

} from '@ant-design/icons';

import {
  fetchComponents as fetchComponentsApi,
  createComponent,
  updateComponent,
  deleteComponent,
  moveComponentUp,
  moveComponentDown,
  fetchPackageDetails as fetchPackageDetailsApi,
  fetchWorkItems as fetchWorkItemsApi,
  createWorkItem,
  updateWorkItem,
  deleteWorkItem,
  moveWorkItemDown,
  moveWorkItemUp,
  syncAllSrNo
} from '../services/quotationTableService';

const { Text, Title } = Typography;

const QuotationTables = () => {
  const location = useLocation();
  const quotationId = location.state?.quotationId;
 
  const [components, setComponents] = useState([]);
  const [createComponentModalVisible, setCreateComponentModalVisible] = useState(false);
  const [newComponentName, setNewComponentName] = useState('');
  const [grandTotal, setGrandTotal] = useState(0);
  const [componentTotals, setComponentTotals] = useState({});

  // Effect to update grand total when component totals change
  useEffect(() => {
    const total = Object.values(componentTotals).reduce((sum, val) => sum + (val || 0), 0);
    setGrandTotal(total);
  }, [componentTotals]);

  useEffect(() => {
    if (quotationId) {
      handleFetchComponents();
    }
  }, [quotationId]);

  const handleFetchComponents = async () => {
    try {
      const componentsData = await fetchComponentsApi(quotationId);
      setComponents(componentsData);
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch components');
    }
  };

 

  const handleUpdateComponentName = async (compId, newName) => {
    try {
      await updateComponent(compId, newName);
      message.success('Component updated');
      handleFetchComponents();
    } catch (error) {
      console.error(error);
      message.error('Failed to update component');
    }
  };

  const confirmDeleteComponent = (compId, compName) => {
    Modal.confirm({
      title: 'Delete Component',
      content: `Are you sure you want to delete component "${compName}"?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => handleDeleteComponent(compId),
    });
  };

  const handleDeleteComponent = async (compId) => {
    try {
      await deleteComponent(compId);
      message.success('Component deleted');
      handleFetchComponents();
    } catch (error) {
      console.error(error);
      message.error('Failed to delete component');
    }
  };

  const handleMoveComponentUp = async (compId) => {
    try {
      await moveComponentUp(compId);
      message.success('Component moved up');
      handleFetchComponents();
    } catch (error) {
      console.error(error);
      message.error('Failed to move component up');
    }
  };

  const handleMoveComponentDown = async (compId) => {
    try {
      await moveComponentDown(compId);
      message.success('Component moved down');
      handleFetchComponents();
    } catch (error) {
      console.error(error);
      message.error('Failed to move component down');
    }
  };

  const handleCreateComponent = async () => {
    try {
      const response = await createComponent({
        PackageID: quotationId,
        ComponentType: newComponentName,
        DisplayOrder: components.length + 1,
      });
      message.success('Component created');
      handleFetchComponents();
      setCreateComponentModalVisible(false);
      setNewComponentName('');
      
    } catch (error) {
      console.error(error);
      message.error('Failed to create component');
    }
  };

  return (
    <div
      style={{ boxShadow: 'none', borderRadius: 8, padding: '16px 24px' }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Quotation Data</Title>
          <Text type="secondary">Manage your quotation data</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateComponentModalVisible(true)}
          >
            Add Space
          </Button>
        </Col>
      </Row>


      {components.map(comp => (
        <ComponentCard
          key={comp.ComponentID}
          component={comp}
          updateComponentName={handleUpdateComponentName}
          deleteComponent={confirmDeleteComponent}
          moveUp={handleMoveComponentUp}
          moveDown={handleMoveComponentDown}
          onWorkItemsChange={handleFetchComponents}
          onTotalChange={(componentId, total) => {
            setComponentTotals(prev => ({
              ...prev,
              [componentId]: total
            }));
          }}
        />
      ))}

      <Card style={{ marginTop: 16, borderRadius: 8 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Text strong style={{ fontSize: 16 }}>Grand Total:</Text>
          </Col>
          <Col>
            <Title level={3} style={{ margin: 0 }}>₹ {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Title>
          </Col>
        </Row>
      </Card>

      <Modal
        title="Add Component"
        visible={createComponentModalVisible}
        onOk={handleCreateComponent}
        onCancel={() => setCreateComponentModalVisible(false)}
        okText="Create"
      >
        <Input
          value={newComponentName}
          onChange={(e) => setNewComponentName(e.target.value)}
          placeholder="Component Name"
        />
      </Modal>
    </div>
  );
};

const ComponentCard = ({ component, updateComponentName, deleteComponent, moveUp, moveDown, onWorkItemsChange, onTotalChange }) => {
  const [editingName, setEditingName] = useState(false);
  const [componentName, setComponentName] = useState(component.ComponentType);
  const [workItemsData, setWorkItemsData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [editingWorkItemId, setEditingWorkItemId] = useState(null);

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const widthRefs = useRef([]);

  useEffect(() => {
    fetchWorkItems();
  }, [component.ComponentID]);

  useEffect(() => {
    const calculateTotal = () => {
      const total = workItemsData.reduce((sum, item) => {
        const manualSqFt = Number(item.SqFt || 0);
        const nos = Number(item.Nos || 0) || 1; // Use 1 if Nos is 0
        const width = Number(item.Width || 0);
        const length = Number(item.Length || 0);
        const price = Number(item.PricePerFeet || 0);
        
        let sqft = 0;
        if ((!item.Length && !item.Width) || (length === 0 && width === 0)) {
          sqft = manualSqFt;
        } else if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
          sqft = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * nos;
        } else {
          sqft = length * width * nos;
        }

        const itemTotal = sqft === 0 ? (nos * price) : (sqft * price);
        return sum + (isNaN(itemTotal) ? 0 : itemTotal);
      }, 0);
      return total;
    };

    const total = calculateTotal();
    if (onTotalChange) onTotalChange(component.ComponentID, total);
  }, [workItemsData, component.ComponentID, onTotalChange]);

  const saveModifiedItems = async () => {
    const modifiedItems = workItemsData.filter(item => item.isModified);
    if (modifiedItems.length === 0) return;
    setIsSaving(true);
    try {
      for (const item of modifiedItems) {
        try {
          const { isModified, ...itemData } = item;
          let SqFt = Number(item.SqFt || 0);
          const nos = Number(item.Nos || 0);
          const width = Number(item.Width || 0);
          const length = Number(item.Length || 0);
          if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
            SqFt = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * (nos || 1);
          } else if ((length !== 0 && width !== 0)) {
            SqFt = length * width * (nos || 1);
          }
          await updateWorkItem(item.WorkItemID, { ...itemData, SqFt });
          setWorkItemsData(prev =>
            prev.map(wi => wi.WorkItemID === item.WorkItemID ? { ...wi, isModified: false } : wi)
          );
        } catch (error) {
          console.error('Failed to autosave item:', item, error);
        }
      }
      onWorkItemsChange(); // Refresh components and grand total
    } finally {
      setIsSaving(false);
    }
  };

  const fetchWorkItems = async () => {
    try {
      const data = await fetchWorkItemsApi(component.ComponentID);
      setWorkItemsData(data);
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch work items');
    }
  };

  const handleWorkItemChange = (id, field, value) => {
    setWorkItemsData(prev =>
      prev.map(item => (item.WorkItemID === id ? { ...item, [field]: value, isModified: true } : item))
    );
  };

  const handleBlur = async (item) => {
    if (item.isModified) {
      await saveWorkItem(item);
    }
  };

  const saveWorkItem = async (item) => {
    try {
      setIsSaving(true);
      const { isModified, ...itemData } = item;
      // Calculate SqFt for payload
      let SqFt = Number(item.SqFt || 0);
      const nos = Number(item.Nos || 0);
      const width = Number(item.Width || 0);
      const length = Number(item.Length || 0);
      if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
        SqFt = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * (nos || 1);
      } else if ((length !== 0 && width !== 0)) {
        SqFt = length * width * (nos || 1);
      }
      await updateWorkItem(item.WorkItemID, { ...itemData, SqFt });
      setWorkItemsData(prev =>
        prev.map(wi => wi.WorkItemID === item.WorkItemID ? { ...itemData, isModified: false } : wi)
      );
      onWorkItemsChange(); // Refresh components and grand total
    } catch (error) {
      console.error(error);
      message.error('Failed to save work item: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmRemoveWorkItem = (id) => {
    Modal.confirm({
      title: 'Delete Work Item',
      content: 'Are you sure you want to delete this work item?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => removeWorkItem(id),
    });
  };

  const removeWorkItem = async (id) => {
    try {
      await deleteWorkItem(id);
      setWorkItemsData(prev => prev.filter(item => item.WorkItemID !== id));
      message.success('Work item removed');
      onWorkItemsChange(); // Refresh components and grand total
    } catch (error) {
      console.error(error);
      message.error('Failed to remove work item');
    }
  };

  // Rename handler to avoid shadowing import
  const handleMoveWorkItemUp = async (item) => {
    try {
      await moveWorkItemUp(item.WorkItemID);
      const data = await fetchWorkItemsApi(component.ComponentID);
      setWorkItemsData(data);
      message.success('Work item moved up');
      onWorkItemsChange(); // Refresh components and grand total
    } catch (error) {
      console.error(error);
      message.error('Failed to move work item up');
    }
  };

  const handleMoveWorkItemDown = async (item) => {
    try {
      await moveWorkItemDown(item.WorkItemID);
      const data = await fetchWorkItemsApi(component.ComponentID);
      setWorkItemsData(data);
      message.success('Work item moved down');
      onWorkItemsChange(); // Refresh components and grand total
    } catch (error) {
      console.error(error);
      message.error('Failed to move work item down');
    }
  };

  const addWorkItem = async () => {
    try {
      const nos = Number(workItemsData.length + 1);
      const newItem = {
        ComponentID: component.ComponentID,
        SrNo: nos,
        CarpentryWork: '-',
        Nos: '0',
        Height: '0',
        Length: '0',
        Width: '0',
        PricePerFeet: '0',
        SqFt: 0
      };
      await createWorkItem(newItem);
      fetchWorkItems();
      message.success('New work item added');
      onWorkItemsChange(); // Refresh components and grand total
    } catch (error) {
      console.error(error);
      message.error('Failed to add work item: ' + (error.response?.data?.error || error.message));
    }
  };

  // CSV download function
  const handleDownloadCSV = () => {
    try {
      const headers = ['Sr No', 'Item', 'Nos', 'Width', 'Length', 'Sq. Ft.', 'Rs./sFt.', 'Total'];
      const csvRows = workItemsData.map((item, index) => {
        const nos = Number(item.Nos || 0) || 1;
        const width = Number(item.Width || 0);
        const length = Number(item.Length || 0);
        const price = Number(item.PricePerFeet || 0);

        // Calculate Sq.Ft.
        let sqft = 0;
        if ((!item.Length && !item.Width) || (length === 0 && width === 0)) {
          sqft = Number(item.SqFt || 0);
        } else if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
          sqft = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * nos;
        } else {
          sqft = length * width * nos;
        }

        // Calculate total
        const total = sqft === 0 ? (nos * price) : (sqft * price);

        return [
          index + 1,
          item.CarpentryWork,
          item.Nos,
          item.Width,
          item.Length,
          sqft.toFixed(2),
          item.PricePerFeet,
          total.toFixed(2)
        ];
      });

      // Combine headers and rows
      const csvContent = [headers, ...csvRows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${component.ComponentType.replace(/[^a-z0-9]/gi, '_')}_items.csv`;
      link.click();

      message.success('CSV downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      message.error('Failed to download CSV');
    }
  };

  // CSV upload function
  const handleUploadCSV = async (file) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        const rows = text.split('\n').map(row =>
          row.split(',').map(cell => cell.replace(/^"(.*)"$/, '$1').trim())
        );

        // Skip header row
        const dataRows = rows.slice(1);

        // Process each row
        for (const row of dataRows) {
          if (row.length >= 7) { // Ensure row has minimum required columns
            const newItem = {
              ComponentID: component.ComponentID,
              CarpentryWork: row[1],
              Nos: row[2],
              Width: row[3],
              Length: row[4],
              PricePerFeet: row[6],
              SqFt: row[5]
            };

            try {
              await createWorkItem(newItem);
            } catch (err) {
              console.error('Error adding work item:', err);
            }
          }
        }

        await fetchWorkItems();
        message.success('CSV data imported successfully');
        onWorkItemsChange();
      };

      reader.readAsText(file);
      return false; // Prevent automatic Upload component upload
    } catch (error) {
      console.error('Error uploading CSV:', error);
      message.error('Failed to upload CSV');
      return false;
    }
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {editingName ? (
            <>
              <Input
                value={componentName}
                onChange={(e) => setComponentName(e.target.value)}
                style={{ marginRight: 8, width: 200 }}
              />
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  updateComponentName(component.ComponentID, componentName);
                  setEditingName(false);
                }}
              >
                Save
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setEditingName(false);
                  setComponentName(component.ComponentType);
                }}
                style={{ marginLeft: 4 }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <span style={{ fontSize: '15px', fontWeight: 600 }}>{component.ComponentType}</span>
              <Button type="text" icon={<EditOutlined />} size="small" onClick={() => setEditingName(true)} style={{ marginLeft: 8 }} />
              <Button type="text" icon={<ArrowUpOutlined />} size="small" onClick={() => moveUp(component.ComponentID)} style={{ marginLeft: 8 }} />
              <Button type="text" icon={<ArrowDownOutlined />} size="small" onClick={() => moveDown(component.ComponentID)} style={{ marginLeft: 8 }} />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => deleteComponent(component.ComponentID, component.ComponentType)}
                style={{ marginLeft: 4 }}
              />
              {isSaving && <Tag color="blue" style={{ marginLeft: 8, borderRadius: '4px' }}>Saving...</Tag>}

            </>
          )}
        </div>} extra={
          <Space size="small">
            {isLocalhost && (
              <>
                <Button
                  type="default"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadCSV}
                >
                  Export CSV
                </Button>
                <Upload
                  beforeUpload={handleUploadCSV}
                  showUploadList={false}
                  accept=".csv"
                >
                  <Button
                    type="default"
                    size="small"
                    icon={<UploadOutlined />}
                  >
                    Import CSV
                  </Button>
                </Upload>
              </>
            )}
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={addWorkItem}
            >
              Add Work Item
            </Button>
            <Button
              type="default"
              size="small"
              icon={<SyncOutlined />}
              onClick={async () => {
                try {
                  setIsSaving(true);
                  // Prepare array of { WorkItemID, SrNo } in current order
                  const srNoList = workItemsData.map((item, idx) => ({
                    WorkItemID: item.WorkItemID,
                    SrNo: idx + 1
                  }));
                  await syncAllSrNo(component.ComponentID, srNoList);
                  message.success('SrNo order synced with backend');
                  fetchWorkItems();
                  onWorkItemsChange();
                } catch (error) {
                  console.error(error);
                  message.error('Failed to sync SrNo order');
                } finally {
                  setIsSaving(false);
                }
              }}
            >
              Sync SrNo Order
            </Button>
          </Space>
        }
      style={{
        marginBottom: 16,
        borderRadius: '8px',
        overflow: 'hidden'
      }}
      bodyStyle={{ padding: 0 }}
      headStyle={{
        padding: '12px 16px',
      }}
    >
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: '1px solid #e0e0e0'
      }}>
        <thead>
          <tr>
            {['Sr No', 'Item', 'Nos', 'Width', 'Length', 'Sq. Ft.', 'Rs./sFt.', 'Total', 'Status', 'Actions'].map((header, index) => (
              header === 'Height' ? null : (
                <th key={header} style={{
                  padding: '4px',
                  fontWeight: 500,
                  fontSize: '13px',
                  textAlign: 'center',
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#f5f5f5',
                  height: '32px',
                  width: index === 1 ? 'auto' : (index === 0 ? '50px' : '80px')
                }}>
                  {header}
                </th>
              )
            ))}
          </tr>
        </thead>
        <tbody>
          {workItemsData.map((item, index) => (
            <tr key={item.WorkItemID} style={{
              backgroundColor: item.isModified ? '#FEF9C3' :
                item.status === 'blue' ? '#E6F4FF' :
                  item.status === 'red' ? '#FFF1F0' :
                    '#fff'
            }}>
              <td style={{
                padding: '0',
                border: '1px solid #e0e0e0',
                textAlign: 'center'
              }}>
                <Typography.Text style={{ fontSize: 13, fontWeight: 500 }}>
                  {index + 1}
                </Typography.Text>
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input.TextArea
                  value={item.CarpentryWork}
                  onChange={(e) => handleWorkItemChange(item.WorkItemID, 'CarpentryWork', e.target.value)}
                  onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                  onBlur={() => {
                    handleBlur(item);
                    setEditingWorkItemId(null);
                  }}
                  disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    '&:focus': {
                      boxShadow: 'none',
                      border: '2px solid #1890ff'
                    }
                  }}
                  autoSize
                />
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input
                  value={Number(item['Nos']).toFixed(0)}
                  type="number"
                  onChange={(e) => handleWorkItemChange(item.WorkItemID, 'Nos', e.target.value)}
                  onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                  onBlur={() => {
                    handleBlur(item);
                    setEditingWorkItemId(null);
                  }}
                  disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    textAlign: 'right'
                  }}
                />
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input
                  ref={el => widthRefs.current[index] = el}
                  value={item['Width']}
                  type="number"
                  onChange={(e) => handleWorkItemChange(item.WorkItemID, 'Width', e.target.value)}
                  onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                  onBlur={() => {
                    handleBlur(item);
                    setEditingWorkItemId(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const next = workItemsData[index + 1];
                      if (next) {
                        setEditingWorkItemId(next.WorkItemID);
                        setTimeout(() => {
                          if (widthRefs.current[index + 1]) {
                            widthRefs.current[index + 1].focus();
                          }
                        }, 0);
                      }
                    }
                  }}
                  disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    textAlign: 'right'
                  }}
                />
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input
                  value={item['Length']}
                  type="number"
                  onChange={(e) => handleWorkItemChange(item.WorkItemID, 'Length', e.target.value)}
                  onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                  onBlur={() => {
                    handleBlur(item);
                    setEditingWorkItemId(null);
                  }}
                  disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    textAlign: 'right'
                  }}
                />
              </td>
              {/* Sq. Ft. */}
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                {((Number(item.Length || 0) === 0) && (Number(item.Width || 0) === 0)) ? (
                  <Input
                    value={item.SqFt || ''}
                    type="number"
                    onChange={e => handleWorkItemChange(item.WorkItemID, 'SqFt', e.target.value)}
                    onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                    onBlur={() => {
                      handleBlur(item);
                      setEditingWorkItemId(null);
                    }}
                    disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                    style={{
                      fontSize: '13px',
                      padding: '4px',
                      border: 'none',
                      borderRadius: '0',
                      backgroundColor: 'transparent',
                      textAlign: 'right',
                    }}
                  />
                ) : (
                  <Input
                    value={(() => {
                      const nos = Number(item.Nos || 0) || 1; // Use 1 if Nos is 0 or empty
                      const width = Number(item.Width || 0);
                      const length = Number(item.Length || 0);
                      if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
                        return ((length === 0 ? 1 : length) * (width === 0 ? 1 : width) * nos).toFixed(2);
                      }
                      return (length * width * nos).toFixed(2);
                    })()}
                    disabled
                    style={{
                      fontSize: '13px',
                      padding: '4px',
                      border: 'none',
                      borderRadius: '0',
                      backgroundColor: 'transparent',
                      textAlign: 'right',
                    }}
                  />
                )}
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input
                  value={item.PricePerFeet}
                  type="number"
                  onChange={(e) => handleWorkItemChange(item.WorkItemID, 'PricePerFeet', e.target.value)}
                  onFocus={() => setEditingWorkItemId(item.WorkItemID)}
                  onBlur={() => {
                    handleBlur(item);
                    setEditingWorkItemId(null);
                  }}
                  disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    textAlign: 'right'
                  }}
                />
              </td>
              <td style={{ padding: '0', border: '1px solid #e0e0e0' }}>
                <Input
                  value={(() => {
                    const manualSqFt = Number(item.SqFt || 0);
                    const nos = Number(item.Nos || 0) || 1; // Use 1 if Nos is 0 or empty
                    const width = Number(item.Width || 0);
                    const length = Number(item.Length || 0);
                    const price = Number(item.PricePerFeet || 0);
                    let sqft = 0;
                    if ((!item.Length && !item.Width) || (Number(item.Length) === 0 && Number(item.Width) === 0)) {
                      sqft = manualSqFt;
                    } else if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
                      // If either length or width is 0, treat the other as 1
                      sqft = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * nos;
                    } else {
                      sqft = length * width * nos;
                    }
                    const total = sqft === 0 ? (nos * price) : (sqft * price);
                    return total.toFixed(2);
                  })()}
                  disabled
                  style={{
                    fontSize: '13px',
                    padding: '4px',
                    border: 'none',
                    borderRadius: '0',
                    backgroundColor: 'transparent',
                    textAlign: 'right',
                  }}
                />
              </td>              <td style={{ padding: '4px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
                <Space size={4}>
                  <Tooltip title="Default">
                    <div
                      onClick={async () => {
                        const value = 'default';
                        setWorkItemsData(prev =>
                          prev.map(wi => wi.WorkItemID === item.WorkItemID ? { ...wi, status: value } : wi)
                        );
                        try {
                          setIsSaving(true);
                          await updateWorkItem(item.WorkItemID, {
                            ...item,
                            status: value
                          });
                        } catch (error) {
                          console.error('Failed to save status:', error);
                          message.error('Failed to update status');
                          setWorkItemsData(prev =>
                            prev.map(wi => wi.WorkItemID === item.WorkItemID ? { ...wi, status: item.status } : wi)
                          );
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: '2px solid #d9d9d9',
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                        opacity: item.status === 'default' ? 1 : 0.5
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Blue">
                    <div
                      onClick={async () => {
                        const value = 'blue';
                        setWorkItemsData(prev =>
                          prev.map(wi => wi.WorkItemID === item.WorkItemID ? { ...wi, status: value } : wi)
                        );
                        try {
                          setIsSaving(true);
                          await updateWorkItem(item.WorkItemID, {
                            ...item,
                            status: value
                          });
                        } catch (error) {
                          console.error('Failed to save status:', error);
                          message.error('Failed to update status');
                          setWorkItemsData(prev =>
                            prev.map(wi => wi.WorkItemID === item.WorkItemID ? { ...wi, status: item.status } : wi)
                          );
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#1890ff',
                        cursor: 'pointer',
                        opacity: item.status === 'blue' ? 1 : 0.5
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Red">
                    <div
                      onClick={async () => {
                        const value = 'red';
                        setWorkItemsData(prev =>
                          prev.map(wi => wi.WorkItemID === item.WorkItemID ? { ...wi, status: value } : wi)
                        );
                        try {
                          setIsSaving(true);
                          await updateWorkItem(item.WorkItemID, {
                            ...item,
                            status: value
                          });
                        } catch (error) {
                          console.error('Failed to save status:', error);
                          message.error('Failed to update status');
                          setWorkItemsData(prev =>
                            prev.map(wi => wi.WorkItemID === item.WorkItemID ? { ...wi, status: item.status } : wi)
                          );
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#ff4d4f',
                        cursor: 'pointer',
                        opacity: item.status === 'red' ? 1 : 0.5
                      }}
                    />
                  </Tooltip>
                </Space>
              </td>
              <td style={{
                padding: '0',
                border: '1px solid #e0e0e0',
                textAlign: 'center'
              }}>
                <Space size="small">
                  <Button
                    type="text"
                    icon={<ArrowUpOutlined />}
                    size="small"
                    onClick={() => handleMoveWorkItemUp(item)}
                    disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  />
                  <Button
                    type="text"
                    icon={<ArrowDownOutlined />}
                    size="small"
                    onClick={() => handleMoveWorkItemDown(item)}
                    disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => confirmRemoveWorkItem(item.WorkItemID)}
                    disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                  />
                  {item.isModified && (
                    <Button
                      type="text"
                      icon={<SaveOutlined />}
                      size="small"
                      onClick={() => saveWorkItem(item)}
                      style={{ color: '#059669' }}
                      disabled={editingWorkItemId !== null && editingWorkItemId !== item.WorkItemID}
                    />
                  )}
                </Space>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={8} style={{ textAlign: 'right', fontWeight: 600, fontSize: 14, background: '#f5f5f5', border: '1px solid #e0e0e0' }}>Component Total</td>
            <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 14, background: '#f5f5f5', border: '1px solid #e0e0e0' }}>
              {(() => {
                return workItemsData.reduce((sum, item) => {
                  const manualSqFt = Number(item.SqFt || 0);
                  const nos = Number(item.Nos || 0) || 1; // Use 1 if Nos is 0 or empty
                  const width = Number(item.Width || 0);
                  const length = Number(item.Length || 0);
                  const price = Number(item.PricePerFeet || 0);
                  let sqft = 0;
                  if ((!item.Length && !item.Width) || (length === 0 && width === 0)) {
                    sqft = manualSqFt;
                  } else if ((length === 0 && width > 0) || (width === 0 && length > 0)) {
                    // If either length or width is 0, treat the other as 1
                    sqft = (length === 0 ? 1 : length) * (width === 0 ? 1 : width) * nos;
                  } else {
                    sqft = length * width * nos;
                  }
                  const total = sqft === 0 ? (nos * price) : (sqft * price);
                  return sum + (isNaN(total) ? 0 : total);
                }, 0).toFixed(2);
              })()}
            </td>
            <td style={{ border: '1px solid #e0e0e0' }}></td>
          </tr>
        </tfoot>
      </table>
    </Card>
  );
};

export default QuotationTables;